import { GameState } from '../../types';
import { MoveInfo } from '../Core/CommonAIUtils';
import { AstraRules, describeMove, moveKey } from '../AstraAI/AstraRules';
import { evaluate, terminalScore, PIECE_VALUES } from '../AstraAI/AstraEvaluator';
import { kingWorlds } from './Observation';

class BudgetEnded extends Error {}
export class BlindSearch {
  private rules=new AstraRules();
  private deadline=0; private nodes=0; private ownLight=true;
  constructor(private maxDepth=6,private maxNodes=Infinity) {}
  private check(){if(Date.now()>=this.deadline || this.nodes>=this.maxNodes)throw new BudgetEnded();}
  private value(s:GameState){return evaluate(s).total*(this.ownLight?1:-1);}
  private ordered(s:GameState,moves:MoveInfo[]):MoveInfo[]{
    return [...moves].sort((a,b)=>{
      const priority=(m:MoveInfo)=>m.move.toPiece?(PIECE_VALUES[m.move.toPiece.type]||0):0;
      return priority(b)-priority(a) || moveKey(a).localeCompare(moveKey(b));
    });
  }
  private action(s:GameState,m:MoveInfo,depth:number,ply:number,alpha:number,beta:number,chain:number):number{
    this.check();
    const outcomes=this.rules.outcomes(s,m);
    if(!outcomes.length)return this.value(s);
    let score=0;
    for(const o of outcomes){
      const same=o.state.isLightTurn===s.isLightTurn && !!o.state.activeBonusTurnPieceId;
      score+=o.probability*this.search(o.state,depth-1+(same&&chain<6?1:0),ply+1,
        outcomes.length>1?-Infinity:alpha,outcomes.length>1?Infinity:beta,same?chain+1:0);
    }
    return score;
  }
  private search(s:GameState,depth:number,ply:number,alpha:number,beta:number,chain:number):number{
    this.check();this.nodes++;
    const end=terminalScore(s,this.ownLight,ply);
    if(end!==undefined)return end;
    if(depth<=0)return this.value(s);
    const moves=this.ordered(s,this.rules.legalMoves(s));
    if(!moves.length)return 0;
    const maximizing=s.isLightTurn===this.ownLight;
    let best=maximizing?-Infinity:Infinity;
    for(const m of moves){
      const score=this.action(s,m,depth,ply,alpha,beta,chain);
      best=maximizing?Math.max(best,score):Math.min(best,score);
      if(maximizing)alpha=Math.max(alpha,best);else beta=Math.min(beta,best);
      if(alpha>=beta)break;
    }
    return best;
  }
  find(observation:GameState,budgetMs:number){
    const started=Date.now();this.deadline=started+Math.max(1,budgetMs*0.9);this.nodes=0;this.ownLight=observation.isLightTurn;
    const worlds=kingWorlds(observation);
    const moves=this.ordered(observation,this.rules.legalMoves(observation));
    if(!moves.length)throw new Error('ZSTR Blind has no legal move');
    if(!worlds.length)throw new Error('ZSTR Blind has no publicly plausible enemy king');
    let selected=moves[0],score=0,depth=0,timedOut=false;
    let rootScores:Array<{description:string;score:number;worldScores:number[]}>=[];
    const iterations:Array<{depth:number;score:number;nodes:number;ms:number}>=[];
    // Score the SAME root action in EVERY hypothesis with full windows. Never
    // average minimax bounds, or compare a move using only favourable worlds.
    for(let d=1;d<=this.maxDepth;d++){
      const scored:typeof rootScores=[];
      try{
        for(const move of moves){
          const values=worlds.map(w=>this.action(w.state,move,d,0,-Infinity,Infinity,0));
          scored.push({description:describeMove(observation,move),score:values.reduce((n,v,i)=>n+v*worlds[i].probability,0),worldScores:values});
        }
        const best=scored.reduce((a,b)=>b.score>a.score?b:a);
        selected=moves[scored.indexOf(best)];score=best.score;depth=d;rootScores=scored;
        iterations.push({depth:d,score,nodes:this.nodes,ms:Date.now()-started});
      }catch(e){
        if(!(e instanceof BudgetEnded))throw e;
        timedOut=true;
        // A shallow partial iteration can only select among moves for which
        // all king hypotheses completed. Deeper partial iterations are ignored.
        if(depth===0 && scored.length){const best=scored.reduce((a,b)=>b.score>a.score?b:a);selected=moves[scored.indexOf(best)];score=best.score;rootScores=scored;}
        break;
      }
    }
    return {move:selected,score,depth,nodes:this.nodes,elapsedMs:Date.now()-started,timedOut,rootScores,iterations,
      candidates:worlds.map(w=>({id:w.candidateId,type:w.type,probability:w.probability})),rootMoves:moves.length};
  }
}
